import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { X, MessageSquare, Loader2, Star } from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const SessionSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muy corto!").max(50, "Muy largo!").required("Requerido"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const initialState = { name: "", greetingMessage: "", farewellMessage: "", isDefault: false, ratingEnabled: false, ratingMessage: "", engine: "wwebjs" };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;
      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}`);
        setWhatsApp(data);
        setSelectedQueueIds(data.queues?.map((q) => q.id) || []);
      } catch (err) { toastError(err); }
    };
    fetchSession();
  }, [whatsAppId]);

  const handleClose = () => { onClose(); setWhatsApp(initialState); };

  const handleSaveWhatsApp = async (values) => {
    const whatsappData = { ...values, queueIds: selectedQueueIds };
    try {
      if (whatsAppId) await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      else await api.post("/whatsapp", whatsappData);
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
    } catch (err) { toastError(err); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex-none">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <span className="text-emerald-500 font-bold text-lg">W</span>
            {whatsAppId ? i18n.t("whatsappModal.title.edit") : i18n.t("whatsappModal.title.add")}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => { handleSaveWhatsApp(values); actions.setSubmitting(false); }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => (
            <Form className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5 bg-slate-50 dark:bg-slate-800/30">
                {/* Basic config */}
                <div>
                  <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Configuración Básica</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t("whatsappModal.form.name")}</label>
                      <Field
                        name="name"
                        autoFocus
                        className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                          touched.name && errors.name ? "border-red-400" : "border-slate-200 dark:border-slate-700"
                        }`}
                      />
                      {touched.name && errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                    </div>
                    <label className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{i18n.t("whatsappModal.form.default")}</span>
                      <div
                        onClick={() => setFieldValue("isDefault", !values.isDefault)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${values.isDefault ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${values.isDefault ? "translate-x-5" : "translate-x-0.5"}`} />
                      </div>
                    </label>
                    <QueueSelect selectedQueueIds={selectedQueueIds} onChange={setSelectedQueueIds} />
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Motor de conexión</label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: "wwebjs", label: "WhatsApp Web", hint: "Estable" },
                          { value: "whaileys", label: "Whaileys", hint: "Beta · ligero" }
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setFieldValue("engine", opt.value)}
                            className={`px-3 py-2 text-sm font-semibold rounded-lg border transition-colors text-left ${
                              (values.engine || "wwebjs") === opt.value
                                ? "bg-indigo-600 border-indigo-600 text-white"
                                : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                            }`}
                          >
                            {opt.label}
                            <span className={`block text-[10px] font-normal ${(values.engine || "wwebjs") === opt.value ? "text-indigo-100" : "text-slate-400"}`}>{opt.hint}</span>
                          </button>
                        ))}
                      </div>
                      {whatsAppId && (
                        <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1.5">
                          Cambiar el motor de una conexión existente requiere reescanear el código QR (o usar código de emparejamiento). Guarda este cambio primero y luego reinicia la sesión desde la lista de Conexiones.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Auto messages */}
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                    <MessageSquare className="w-3.5 h-3.5" /> Mensajes Automáticos
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t("queueModal.form.greetingMessage")}</label>
                      <Field
                        as="textarea"
                        name="greetingMessage"
                        rows={3}
                        placeholder="Hola! Bienvenido..."
                        className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t("whatsappModal.form.farewellMessage")}</label>
                      <Field
                        as="textarea"
                        name="farewellMessage"
                        rows={3}
                        placeholder="Gracias por contactarnos. ¡Hasta pronto!"
                        className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* CSAT Survey Config */}
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                    <Star className="w-3.5 h-3.5 text-amber-500" /> Encuesta de Calificación (CSAT)
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Habilitar Encuesta al Cerrar Ticket</span>
                      <div
                        onClick={() => setFieldValue("ratingEnabled", !values.ratingEnabled)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${values.ratingEnabled ? "bg-emerald-600" : "bg-slate-300 dark:bg-slate-600"}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${values.ratingEnabled ? "translate-x-5" : "translate-x-0.5"}`} />
                      </div>
                    </label>

                    {values.ratingEnabled && (
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Mensaje de Calificación</label>
                        <Field
                          as="textarea"
                          name="ratingMessage"
                          rows={3}
                          placeholder="Por favor califica nuestro servicio del 1 al 5 siendo 5 excelente."
                          className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">El usuario responderá con un número del 1 al 5. El sistema guardará la calificación automáticamente.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex-none">
                <button type="button" onClick={handleClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  {i18n.t("whatsappModal.buttons.cancel")}
                </button>
                <button type="submit" disabled={isSubmitting} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {whatsAppId ? i18n.t("whatsappModal.buttons.okEdit") : i18n.t("whatsappModal.buttons.okAdd")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default React.memo(WhatsAppModal);

